// S8 — Templates (/catalogue/templates, Module 01 §4 + §3.8), on the
// revision-6 register. A gallery grouped by kind (product / bom / routing) with
// a compact payload preview; "Use" deep-links into the matching create flow;
// custom templates can be saved from an existing entity (from_entity) or as an
// empty starter. Read is open to all office roles; save/archive need
// `catalogue.template.manage` (facility_admin, group_admin) and "Use" needs an
// authoring role (planner included) — hidden otherwise, with the server as the
// authoritative gate. Every request state has the §3.9 treatment.
//
// Grouping is headings + space, not a Card per group (§3.6 rule 3, and Cards
// never nest): the template TILES are the boxes that earn their border, since
// each one carries actions.

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { StatusChip } from "../../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  useArchiveTemplate,
  useCreateTemplate,
  useProducts,
  useTemplates,
} from "../hooks/catalogue-queries";
import { useCatalogueCaps } from "./uom-templates/caps";
import {
  groupTemplatesByKind,
  messageFor,
  stashPendingTemplate,
  summarizeTemplatePayload,
  TEMPLATE_KINDS,
  templateUseTarget,
  visibleTemplates,
  type CatalogueTemplate,
  type TemplateKind,
  type TemplatePreviewItem,
} from "./uom-templates/helpers";
import "../catalogue.css";

export function TemplatesScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const caps = useCatalogueCaps();
  const templatesQuery = useTemplates();
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);

  // 401 → sign-in in an effect (not during render) to avoid a React
  // "update during render" warning — mirrors product-detail / products-list.
  useEffect(() => {
    if (templatesQuery.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [templatesQuery.error, navigate]);

  if (templatesQuery.error instanceof UnauthorizedError) {
    return null;
  }

  const onUse = (tpl: CatalogueTemplate) => {
    stashPendingTemplate({ kind: tpl.kind, code: tpl.code });
    void navigate({
      to: templateUseTarget(tpl.kind) === "product" ? "/products/new" : "/catalogue/products",
    });
  };

  const groups =
    templatesQuery.data !== undefined
      ? groupTemplatesByKind(visibleTemplates(templatesQuery.data.items, showArchived))
      : [];
  const totalVisible = groups.reduce((n, g) => n + g.templates.length, 0);
  const hasAny = (templatesQuery.data?.items.length ?? 0) > 0;

  return (
    <div className="cat-screen">
      <PageHeader
        title={t("catalogue.templates.title")}
        crumbs={[{ label: t("nav.catalogue"), to: "/catalogue/products" }]}
        actions={
          caps.canManageTemplates && !creating ? (
            <Button
              variant="primary"
              data-testid="new-template"
              onClick={() => {
                setCreating(true);
              }}
            >
              {t("catalogue.templates.newTemplate")}
            </Button>
          ) : undefined
        }
      />
      <p className="cat-note">{t("catalogue.templates.subtitle")}</p>

      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="template-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setNotice(null);
          }}
        >
          {notice.text}
        </Banner>
      ) : null}

      {caps.canManageTemplates && creating ? (
        <NewTemplateForm
          onDone={(createdName) => {
            if (createdName !== undefined) {
              setNotice({
                kind: "ok",
                text: t("catalogue.templates.created", { name: createdName }),
              });
            }
            setCreating(false);
          }}
        />
      ) : null}

      <div className="cat-row">
        <label className="cat-check cat-fld">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
            }}
          />
          <span>{t("catalogue.templates.showArchived")}</span>
        </label>
      </div>

      {templatesQuery.isPending ? (
        <div className="uomt-cards">
          {[0, 1, 2].map((i) => (
            <CardSkeleton key={i} lines={3} label={t("catalogue.state.loading")} />
          ))}
        </div>
      ) : templatesQuery.error ? (
        <ErrorState
          title={t("catalogue.state.errorTitle")}
          detail={messageFor(templatesQuery.error, t("catalogue.state.errorDetail"))}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => void templatesQuery.refetch()}
          requestId={requestIdOf(templatesQuery.error)}
          requestIdLabel={t("catalogue.state.requestId")}
        />
      ) : !hasAny ? (
        <EmptyState
          title={t("catalogue.templates.emptyTitle")}
          body={t("catalogue.templates.emptyBody")}
          ctaLabel={caps.canManageTemplates ? t("catalogue.templates.newTemplate") : undefined}
          onCta={
            caps.canManageTemplates
              ? () => {
                  setCreating(true);
                }
              : undefined
          }
        />
      ) : totalVisible === 0 ? (
        <EmptyState
          title={t("catalogue.templates.archivedOnlyTitle")}
          body={t("catalogue.templates.archivedOnlyBody")}
          ctaLabel={t("catalogue.templates.showArchived")}
          onCta={() => {
            setShowArchived(true);
          }}
        />
      ) : (
        <div className="uomt-gallery">
          {groups.map((group) => (
            <section key={group.kind} aria-label={t(`catalogue.templates.group.${group.kind}`)}>
              <h2 className="uomt-group-title">{t(`catalogue.templates.group.${group.kind}`)}</h2>
              <div className="uomt-cards">
                {group.templates.map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    template={tpl}
                    canUse={caps.canUseTemplates}
                    canManage={caps.canManageTemplates}
                    onUse={() => {
                      onUse(tpl);
                    }}
                    onNotice={setNotice}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  canUse,
  canManage,
  onUse,
  onNotice,
}: {
  template: CatalogueTemplate;
  canUse: boolean;
  canManage: boolean;
  onUse: () => void;
  onNotice: (n: { kind: "ok" | "bad"; text: string }) => void;
}) {
  const { t } = useTranslation();
  const archive = useArchiveTemplate(template.id);
  const [showPayload, setShowPayload] = useState(false);
  const preview = summarizeTemplatePayload(template.kind, template.payload);

  return (
    <article className="uomt-card" data-testid="template-card">
      <div className="uomt-card-head">
        <div>
          <div className="uomt-card-name">{template.name}</div>
          <div className="uomt-card-code">{template.code}</div>
        </div>
        <div className="uomt-badges">
          <StatusChip variant="neutral" dot={false}>
            {t(`catalogue.templates.kind.${template.kind}`)}
          </StatusChip>
          <StatusChip variant="neutral" dot={false}>
            {t(`catalogue.templates.source.${template.source}`)}
          </StatusChip>
          <StatusBadge status={template.status} />
        </div>
      </div>

      {preview.length > 0 ? (
        <div className="uomt-chips">
          {preview.map((item, i) => (
            <PreviewChip key={i} item={item} />
          ))}
        </div>
      ) : (
        <p className="uomt-note">{t("catalogue.templates.preview.none")}</p>
      )}

      <button
        type="button"
        className="ds-lnk"
        aria-expanded={showPayload}
        onClick={() => {
          setShowPayload((v) => !v);
        }}
      >
        {showPayload ? t("catalogue.templates.hidePayload") : t("catalogue.templates.viewPayload")}
      </button>
      {showPayload ? (
        <pre className="uomt-payload" data-testid="template-payload">
          {JSON.stringify(template.payload, null, 2)}
        </pre>
      ) : null}

      {canUse || (canManage && template.status === "active") ? (
        <div className="uomt-card-actions">
          {canUse ? (
            /* NOT a primary. There are as many "Use" buttons as there are
             * template tiles, and §1 rule 1 allows one primary per VIEW — a
             * grid of accented buttons is the "boxes all over the place" the
             * owner objected to. The page's one primary is "New template". */
            <Button
              size="sm"
              title={
                templateUseTarget(template.kind) === "structure"
                  ? t("catalogue.templates.useStructureNote", {
                      kind: t(`catalogue.templates.kind.${template.kind}`),
                    })
                  : t("catalogue.templates.useProductNote")
              }
              onClick={onUse}
            >
              {t("catalogue.templates.use")}
            </Button>
          ) : null}
          {canManage && template.status === "active" ? (
            <Button
              size="sm"
              busy={archive.isPending}
              onClick={() => {
                archive.mutate(undefined, {
                  onSuccess: () => {
                    onNotice({
                      kind: "ok",
                      text: t("catalogue.templates.archived", { name: template.name }),
                    });
                  },
                  onError: (e) => {
                    onNotice({
                      kind: "bad",
                      text: messageFor(e, t("catalogue.templates.archiveFailed")),
                    });
                  },
                });
              }}
            >
              {t("catalogue.templates.archive")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function PreviewChip({ item }: { item: TemplatePreviewItem }) {
  const { t } = useTranslation();
  if (item.type === "count") {
    return <span className="uomt-chip">{t(item.labelKey, { count: item.count })}</span>;
  }
  if (item.type === "flag") {
    return <span className="uomt-chip">{t(item.labelKey)}</span>;
  }
  if (item.type === "enum") {
    /* `defaultValue` so a pack that invents an enum member we have no name for
       degrades to the raw token rather than to a blank chip — visibly odd, not
       silently empty. */
    return (
      <span className="uomt-chip">
        <span>{t(item.labelKey)}</span>
        <span className="uomt-chip-value">{enumLabel(t, item.valueKeyPrefix, item.value)}</span>
      </span>
    );
  }
  return (
    <span className="uomt-chip">
      <span>{t(item.labelKey)}</span>
      <span className="uomt-chip-value">{item.value}</span>
    </span>
  );
}

function NewTemplateForm({ onDone }: { onDone: (createdName: string | undefined) => void }) {
  const { t } = useTranslation();
  const create = useCreateTemplate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<TemplateKind>("product");
  const [mode, setMode] = useState<"empty" | "snapshot">("empty");
  const [sourceProductId, setSourceProductId] = useState("");
  const [sourceEntityId, setSourceEntityId] = useState("");

  const productsQuery = useProducts();
  const products = productsQuery.data?.items ?? [];

  const snapshotId = kind === "product" ? sourceProductId : sourceEntityId.trim();
  const needsSource = mode === "snapshot";
  const canSubmit =
    name.trim().length > 0 &&
    code.trim().length > 0 &&
    (!needsSource || snapshotId.length > 0) &&
    !create.isPending;

  return (
    <form
      className="ds-card cat-form"
      data-testid="template-form"
      aria-label={t("catalogue.templates.newTemplate")}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const trimmedName = name.trim();
        const base = { code: code.trim(), name: trimmedName, kind };
        const body =
          needsSource && snapshotId.length > 0
            ? { ...base, from_entity: { kind, id: snapshotId } }
            : base;
        create.mutate(body, {
          onSuccess: () => {
            onDone(trimmedName);
          },
        });
      }}
    >
      <div className="cat-fld">
        <label htmlFor="uomt-tpl-name">{t("catalogue.templates.form.nameLabel")}</label>
        <input
          id="uomt-tpl-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-tpl-code">{t("catalogue.templates.form.codeLabel")}</label>
        <input
          id="uomt-tpl-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
          required
        />
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-tpl-kind">{t("catalogue.templates.form.kindLabel")}</label>
        <select
          id="uomt-tpl-kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as TemplateKind);
          }}
        >
          {TEMPLATE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`catalogue.templates.kind.${k}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="cat-fld">
        <label htmlFor="uomt-tpl-mode">{t("catalogue.templates.form.modeLabel")}</label>
        <select
          id="uomt-tpl-mode"
          value={mode}
          onChange={(e) => {
            setMode(e.target.value as "empty" | "snapshot");
          }}
        >
          <option value="empty">{t("catalogue.templates.form.modeEmpty")}</option>
          <option value="snapshot">{t("catalogue.templates.form.modeSnapshot")}</option>
        </select>
      </div>

      {needsSource && kind === "product" ? (
        <div className="cat-fld">
          <label htmlFor="uomt-tpl-src-product">
            {t("catalogue.templates.form.sourceProductLabel")}
          </label>
          <select
            id="uomt-tpl-src-product"
            value={sourceProductId}
            onChange={(e) => {
              setSourceProductId(e.target.value);
            }}
            required
          >
            <option value="" disabled>
              {productsQuery.isPending
                ? t("catalogue.templates.form.loadingProducts")
                : t("catalogue.uom.selectPlaceholder")}
            </option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} — {p.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {needsSource && kind !== "product" ? (
        <div className="cat-fld">
          <label htmlFor="uomt-tpl-src-entity">
            {t("catalogue.templates.form.sourceEntityLabel")}
          </label>
          <input
            id="uomt-tpl-src-entity"
            value={sourceEntityId}
            onChange={(e) => {
              setSourceEntityId(e.target.value);
            }}
            required
          />
          <span className="cat-hint">{t("catalogue.templates.form.sourceEntityHint")}</span>
        </div>
      ) : null}

      <div className="cat-form-actions">
        <Button
          type="button"
          onClick={() => {
            onDone(undefined);
          }}
        >
          {t("catalogue.templates.form.cancel")}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending} disabled={!canSubmit}>
          {t("catalogue.templates.form.create")}
        </Button>
      </div>

      {create.isError ? (
        <div className="cat-form-wide">
          <Banner kind="bad">
            {messageFor(create.error, t("catalogue.templates.form.createFailed"))}
          </Banner>
        </div>
      ) : null}
    </form>
  );
}
